import {
  PriceRequestType,
  PriceResponseType,
  UpdateClosureObjectType,
} from './../types/bookingTypes';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  BookingType,
  UpdateBookingType,
  MainObjectShort,
  UpdateBookingPayloadData,
} from '../types/bookingTypes';
import { PayloadDataWithId } from '../types/@types';

type BookingState = {
  bookingList: BookingType[] | undefined;
  chessObjectsList: MainObjectShort[] | null;
  isTelephoneValid: boolean;
  chessModalWindow: {
    isOpen: boolean;
    booking: BookingType | null;
    bookingClassName: string;
  };
  closureObjectsList: UpdateClosureObjectType[];
  bookingPrice: PriceResponseType | null;
  priceError: string | null;
  bookingListLoading: boolean;
  bookingListError: string | null;
};

const initialState: BookingState = {
  bookingList: undefined,
  chessObjectsList: null,
  isTelephoneValid: false,
  chessModalWindow: {
    isOpen: false,
    booking: null,
    bookingClassName: '',
  },
  closureObjectsList: [],
  bookingPrice: null,
  priceError: null,
  bookingListLoading: false,
  bookingListError: null,
};

const BookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    getBookingList(state, __: PayloadAction<{ start: string; end: string }>) {
      state.bookingListLoading = true;
      state.bookingListError = null;
    },
    setBookingList(state, action: PayloadAction<BookingType[]>) {
      state.bookingList = action.payload;
      state.bookingListLoading = false;
      state.bookingListError = null;
    },
    setBookingListError(state, action: PayloadAction<string>) {
      state.bookingListLoading = false;
      state.bookingListError = action.payload;
    },
    addBookingToList(state, action: PayloadAction<BookingType>) {
      if (!state.bookingList) {
        state.bookingList = [action.payload];
      } else {
        state.bookingList.push(action.payload);
      }
    },
    getChessObjects(_, __) {},
    setChessObjectsList(
      state,
      action: PayloadAction<MainObjectShort[] | null>,
    ) {
      state.chessObjectsList = action.payload;
    },
    updateCurrentBooking(_, __: PayloadAction<UpdateBookingPayloadData>) {},
    updateBookingInList(state, action: PayloadAction<BookingType>) {
      const index = state.bookingList?.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (state.bookingList && index !== undefined && index !== -1) {
        state.bookingList[index] = action.payload;
      } else {
        state.bookingList?.push(action.payload);
      }
    },
    updateViewedStatus(_, __: PayloadAction<number>) {},
    removeBooking(_, __: PayloadAction<number>) {},
    removeBookingFromList(state, action: PayloadAction<number>) {
      state.bookingList =
        state.bookingList &&
        state.bookingList.filter((note) => note.id !== action.payload);
    },
    createBooking(_, __: PayloadAction<UpdateBookingType>) {},
    setIsTelephoneValid(state, action: PayloadAction<boolean>) {
      state.isTelephoneValid = action.payload;
    },
    closeChessModalWindow(state) {
      state.chessModalWindow = {
        isOpen: false,
        booking: null,
        bookingClassName: '',
      };
    },
    setChessModalWindow(
      state,
      action: PayloadAction<{ booking: BookingType; bookingClassName: string }>,
    ) {
      state.chessModalWindow = {
        isOpen: true,
        booking: action.payload.booking,
        bookingClassName: action.payload.bookingClassName,
      };
    },
    createClosureObject(_, __: PayloadAction<UpdateClosureObjectType>) {},
    addClosureObjectToList(
      state,
      action: PayloadAction<UpdateClosureObjectType>,
    ) {
      if (!state.closureObjectsList) {
        state.closureObjectsList = [action.payload];
      } else {
        state.closureObjectsList.push(action.payload);
      }
    },
    getClosureObjectsList(_, __) {},
    setClosureObjectsList(
      state,
      action: PayloadAction<UpdateClosureObjectType[]>,
    ) {
      state.closureObjectsList = action.payload;
    },
    removeClosureObject(_, __: PayloadAction<number>) {},
    removeClosureObjectFromList(state, action: PayloadAction<number>) {
      state.closureObjectsList =
        state.closureObjectsList &&
        state.closureObjectsList.filter((note) => note.id !== action.payload);
    },
    updateCurrentClosure(
      _,
      __: PayloadAction<PayloadDataWithId<UpdateClosureObjectType>>,
    ) {},

    getTotalPrice(_, __: PayloadAction<PriceRequestType>) {},

    setTotalPrice(state, action: PayloadAction<PriceResponseType | null>) {
      state.bookingPrice = action.payload;
    },

    setPriceError(state, action: PayloadAction<string>) {
      state.priceError = action.payload;
    },

    clearPriceError(state, __) {
      state.priceError = null;
    },
  },
});

export const {
  getBookingList,
  setBookingList,
  setBookingListError,
  getChessObjects,
  setChessObjectsList,
  updateCurrentBooking,
  removeBooking,
  removeBookingFromList,
  createBooking,
  setIsTelephoneValid,
  closeChessModalWindow,
  setChessModalWindow,
  updateViewedStatus,
  createClosureObject,
  addClosureObjectToList,
  getClosureObjectsList,
  setClosureObjectsList,
  removeClosureObject,
  removeClosureObjectFromList,
  updateCurrentClosure,
  addBookingToList,
  updateBookingInList,
  getTotalPrice,
  setTotalPrice,
  setPriceError,
  clearPriceError,
} = BookingSlice.actions;

export default BookingSlice.reducer;

export const bookingName = BookingSlice.name;

export const BookingSelectors = {
  getBookingList: (state: RootState) => state.booking.bookingList,
  getBookingListLoading: (state: RootState) => state.booking.bookingListLoading,
  getBookingListError: (state: RootState) => state.booking.bookingListError,
  getChessObjects: (state: RootState) => state.booking.chessObjectsList,
  getIsTelephoneValid: (state: RootState) => state.booking.isTelephoneValid,
  getChessModalWindow: (state: RootState) => state.booking.chessModalWindow,
  getClosureObjectsList: (state: RootState) => state.booking.closureObjectsList,
  getBookingPrice: (state: RootState) => state.booking.bookingPrice,
  getPriceError: (state: RootState) => state.booking.priceError,
};
